// THE ROW TYPES AND THE API SHAPES — one declaration, used by the server and the SPA.
//
// Every interface below either mirrors a table in src/server/db/schema.sql (the row types) or a
// response documented in BUILD-BRIEF §4 (the API shapes). When the schema changes, this file
// changes with it in the same commit; a route that answers a shape not declared here is a bug.
//
// No imports: this file must be loadable from anywhere.

// ═══ small vocabularies ═════════════════════════════════════════════════════

export type CreatedByKind = 'operator' | 'agent' | 'import';
export type SubjectKind = 'person' | 'organisation';

export type OrganisationStatus = 'active' | 'dormant' | 'archived';
export type PersonStatus = 'held' | 'candidate' | 'archived';
export type PersonLanguage = 'de' | 'fr' | 'en' | 'it';
export type Register = 'sie' | 'du';
export type Tier = 'A' | 'B' | 'C';

export type AffiliationStatus = 'held' | 'proposed' | 'retracted';
/**
 * What KIND of tie an affiliation is (BUILD-BRIEF-R7 §2). Round 7 asks two questions of it that
 * one undifferentiated column cannot answer: how many governance seats a person holds
 * (`board_mandates_count`, §1.3) and which affiliation is the primary one the job block reads
 * (the current `employment` one, §2). 'external_mandate' is never inferred from a title — it
 * needs a second organisation or the register — so only a person or a held proposal writes it.
 */
export type AffiliationKind = 'employment' | 'governance' | 'external_mandate';
export type AffiliationSource =
  | 'official' | 'professional' | 'meeting_label' | 'mail_signature' | 'snapshot' | 'surfe' | 'manual';

export type FactStatus = 'current' | 'superseded' | 'retracted';
/** 'surfe' since round 7 (BUILD-BRIEF-R7 §5.2): a broker's answer kept as a snapshot of kind 'surfe'; the ref is its id. */
export type EvidenceKind = 'url' | 'snapshot' | 'interaction' | 'document' | 'manual' | 'surfe';
/**
 * HOW a value came to be (BUILD-BRIEF-R3 §0). The three must never look the same:
 *   read      a source says it, and the fact quotes that source
 *   estimated an agent inferred it, and carries a confidence at or below 0.7
 *   computed  the CRM derived it from other cells or from signals — arithmetic, no claim mark
 *   declared  the operator typed it
 */
/** How a value came to be — and `not_found`: searched on `as_of` in the sources the evidence names, and absent. A verdict, not an empty cell. */
export type FactMethod = 'read' | 'estimated' | 'computed' | 'declared' | 'not_found';

export type InteractionKind = 'mail_in' | 'mail_out' | 'meeting' | 'call' | 'note' | 'post' | 'signal';
export type ParticipantRole = 'host' | 'attendee' | 'from' | 'to' | 'cc';

export type CommitmentOwner = 'us' | 'them';
export type CommitmentStatus = 'open' | 'discharged' | 'dropped';

export type Stage =
  | 'lead' | 'qualified' | 'scoping' | 'proposal' | 'negotiation' | 'contracting' | 'won' | 'lost';

/** The board's column order. `won` and `lost` are outcomes, not columns. */
export const OPEN_STAGES: Stage[] = ['lead', 'qualified', 'scoping', 'proposal', 'negotiation', 'contracting'];
/** A deal that has sat in its stage longer than this is stale (BUILD-BRIEF §4). */
export const STALE_DAYS = 14;

export type ProposalKind =
  | 'fact' | 'affiliation' | 'touchpoint' | 'commitment' | 'persona'
  | 'draft' | 'candidate' | 'signal_action' | 'organisation' | 'person'
  // round 2: a proposed change to a weight, a rule document or a skill body. Its applier
  // writes app_settings + settings_history, never a record table.
  | 'rule_change'
  // round 3: two organisations the finder believes to be one (or parent and unit). The operator
  // decides — merge, relate or dismiss — and the applier under `utils/appliers/dedupe.ts` is the
  // only code that acts on it (DedupePayload below).
  | 'dedupe'
  // round 3b: a website for an organisation that has none (BUILD-BRIEF-R3b §2c). Its applier
  // (`utils/appliers/domain.ts`) appends the domain and fetches the logo; undoable.
  | 'domain'
  // round 5: who works with whom — a payer, referrer, funder, umbrella, partner, accreditor,
  // vendor, consultant or auditor relation on an organisation. Its applier
  // (`utils/appliers/link.ts`) is the one writer of `organisation_links` (BUILD-BRIEF-R5 §2, §3.5).
  | 'link';
export type ProposalStatus = 'unheld' | 'held' | 'amended' | 'refused';
export type RefuseReason =
  | 'duplicate' | 'wrong_evidence' | 'stale' | 'not_relevant' | 'out_of_scope' | 'other';

export type MediaKind = 'logo' | 'avatar';
export type MediaStatus = 'ok' | 'not_found' | 'failed' | 'blocked';
export type MediaSource =
  | 'gravatar' | 'og_image' | 'favicon' | 'apple_touch_icon' | 'upload' | 'surfe' | 'scout';

/** Every table that carries personal data carries who put it there (DESIGN §5). */
export interface Provenance {
  created_at: string;
  created_by_kind: CreatedByKind;
  created_by: number | null;
}

/** One piece of evidence behind a proposal or a fact. `quote` is verbatim, never paraphrased. */
export interface Evidence {
  kind: EvidenceKind;
  ref?: string | null;
  quote?: string | null;
  as_of?: string | null;
}

// ═══ rows ═══════════════════════════════════════════════════════════════════

export interface Organisation extends Provenance {
  id: number;
  uuid: string;
  ref: string;
  name: string;
  legal_name: string | null;
  legal_form: string | null;
  uid: string | null;
  domains: string[];
  aliases: string[];
  seat: string | null;
  canton: string | null;
  sector: string | null;
  parent_id: number | null;
  status: OrganisationStatus;
  is_client: boolean;
  signet_client_id: number | null;
  do_not_contact: boolean;
  notes: string | null;
  /** Set by a held `dedupe` merge: this row is archived and everything it had now points at
   *  that organisation. NULL on every row that was never merged away. */
  merged_into?: number | null;
  /** When a `deep_research` run last ended done on this organisation — stamped by the harness
   *  runner, not a fact and not an agent write. NULL until one did. */
  last_researched_at?: string | null;
  updated_at: string;
}

export interface Person extends Provenance {
  id: number;
  uuid: string;
  ref: string;
  first_name: string | null;
  last_name: string | null;
  name: string;
  language: PersonLanguage;
  register: Register | null;
  tier: Tier;
  cadence_days: number;
  emails: string[];
  phones: string[];
  linkedin_url: string | null;
  location: string | null;
  status: PersonStatus;
  do_not_contact: boolean;
  notes: string | null;
  /** Set by a person merge (`services/personMerge.ts`): this row is archived and everything it
   *  had now points at that person. NULL on every row that was never merged away — the live-row
   *  filter is `status <> 'archived' AND merged_into IS NULL`, exactly as it is for an
   *  organisation. The archived row keeps its PER- ref, so a text ref to it still resolves. */
  merged_into?: number | null;
  /** Round 7 (§5.2, LPD art. 19): the day this person must be told that a broker supplied their
   *  professional e-mail — stamped when that e-mail is HELD. NULL means there is no duty, never
   *  "not sent yet", so a page must not read it as an overdue notice. */
  notice_due_at?: string | null;
  /** When the operator clicked send on that notice. The `notices.due` job only DRAFTS — `send` is
   *  not a capability (P9) — so nothing but a click ever stamps this. */
  notice_sent_at?: string | null;
  updated_at: string;
}

export interface Affiliation extends Provenance {
  id: number;
  person_id: number;
  organisation_id: number;
  role_title: string | null;
  started_on: string | null;
  ended_on: string | null;
  is_current: boolean;
  /** Round 7 (§2). Every row written before the distinction existed is 'employment' — the truth
   *  about what was recorded, not a guess; `scripts/affiliation-kind-dry-run.ts` prints what
   *  TITLE_MAP would mark 'governance', a person reads the list, `--apply` writes it. */
  kind: AffiliationKind;
  status: AffiliationStatus;
  source_kind: AffiliationSource | null;
  source_url: string | null;
  source_note: string | null;
}

/** An affiliation joined with the names either side needs to render. */
export interface AffiliationView extends Affiliation {
  organisation_ref?: string;
  organisation_name?: string;
  person_ref?: string;
  person_name?: string;
}

export interface Fact extends Provenance {
  id: number;
  subject_kind: SubjectKind;
  subject_id: number;
  key: string;
  value: string | null;
  as_of: string | null;
  status: FactStatus;
  superseded_by: number | null;
  evidence_kind: EvidenceKind | null;
  evidence_ref: string | null;
  quote: string | null;
  confidence: number | null;
  /** Round 3. Existing rows default to 'read'; the operator's own writes are 'declared'. */
  method: FactMethod;
  /** How exact a numeric cell's value is (raster.v1 NumericQualifier); null or absent = exact. */
  qualifier?: 'at_least' | 'at_most' | 'approx' | null;
  confirmed_by: number | null;
  confirmed_at: string | null;
}

export interface Interaction extends Provenance {
  id: number;
  uuid: string;
  ref: string;
  kind: InteractionKind;
  occurred_on: string;
  occurred_at: string | null;
  date_confirmed: boolean;
  title: string;
  summary: string | null;
  body: string | null;
  organisation_id: number | null;
  source_kind: string | null;
  source_ref: string | null;
  thread_id: string | null;
}

export interface InteractionParticipant {
  id: number;
  interaction_id: number;
  person_id: number | null;
  raw_name: string | null;
  role: ParticipantRole;
}

/** What a POST /api/interactions body carries for each participant. */
export interface ParticipantInput {
  person_id?: number | null;
  raw_name?: string | null;
  role?: ParticipantRole;
}

export interface InteractionDetail extends Interaction {
  participants: Array<InteractionParticipant & { person_name?: string | null; person_ref?: string | null }>;
  organisation_name?: string | null;
}

export interface Commitment extends Provenance {
  id: number;
  ref: string;
  owner: CommitmentOwner;
  text: string;
  due_on: string | null;
  person_id: number | null;
  organisation_id: number | null;
  source_interaction_id: number | null;
  status: CommitmentStatus;
  discharged_at: string | null;
  discharged_by_interaction_id: number | null;
  discharge_note: string | null;
}

export interface CommitmentView extends Commitment {
  person_name?: string | null;
  organisation_name?: string | null;
  source_ref?: string | null;
}

export interface Opportunity extends Provenance {
  id: number;
  uuid: string;
  ref: string;
  organisation_id: number;
  title: string;
  face_value: string | null;      // NUMERIC arrives as a string; never parse it for display
  currency: string;
  probability: number | null;
  stage: Stage;
  stage_entered_at: string;
  next_step: string | null;
  next_step_due: string | null;
  loss_reason: string | null;
  revisit_on: string | null;
  signet_engagement_code: string | null;
  primary_person_id: number | null;
  updated_at: string;
}

/** A deal as the board renders it: the row plus what the column needs to say about it. */
export interface OpportunityCard extends Opportunity {
  organisation_name: string;
  primary_person_name: string | null;
  days_in_stage: number;
  stale: boolean;
}

export interface OpportunityEvent {
  id: number;
  opportunity_id: number;
  from_stage: Stage | null;
  to_stage: Stage;
  reason: string | null;
  quote: string | null;
  source_ref: string | null;
  created_at: string;
  created_by: number | null;
}

/** What one chained hold added beside the proposal the operator actually clicked (2026-09-05):
 *  the organisation proposal `holdProposal` held FIRST, in the same transaction, because the
 *  dependent named it and it had nothing else to stand on. Undoing the dependent does not undo
 *  these. */
export interface AlsoHeld {
  id: number;
  ref: string;
  kind: ProposalKind;
  applied_ref: string | null;
}

/** An unheld proposal's organisation dependency (the queue's own view of what `holdProposal`
 *  would chain-hold first): the organisation proposal it names, still pending or already
 *  resolved. Always `kind: 'organisation'` today — an affiliation, a person or a link is the
 *  only shapes that carry one. */
export interface ProposalDependency {
  id: number;
  ref: string;
  kind: 'organisation';
  name: string | null;
  status: ProposalStatus;
}

export interface Proposal extends Provenance {
  id: number;
  ref: string;
  kind: ProposalKind;
  subject_kind: SubjectKind | null;
  subject_id: number | null;
  payload: Record<string, unknown>;
  evidence: Evidence[];
  principal: string;
  run_id: number | null;
  status: ProposalStatus;
  refuse_reason: RefuseReason | null;
  reviewer_note: string | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  applied_ref: string | null;
  payload_held: Record<string, unknown> | null;
  // ── joined for the queue row; absent on a bare SELECT * ───────────────────
  /** The subject's display name, resolved through subject_kind + subject_id. */
  subject_label?: string | null;
  subject_ref?: string | null;
  /** Names resolved out of the payload's person_id / organisation_id, for the rail line. */
  payload_person_name?: string | null;
  payload_person_ref?: string | null;
  payload_organisation_name?: string | null;
  payload_organisation_ref?: string | null;
  evidence_count?: number;
  /**
   * One flag per row (ATTEST: one kind, one descriptor, one flag). The queue narrows this to
   * its own union (`collides | no_evidence | not_on_record | null`); it is left open here so a
   * later producer can name a flag this file has not heard of yet.
   */
  flag?: string | null;
  /** The organisation this proposal names but does not yet have (empty or absent when its kind
   *  carries none, it names none, or it names one already held). GET /api/proposals only. */
  depends_on?: ProposalDependency[] | null;
  /** An unheld `organisation` proposal only: how many other unheld rows on this page name it. */
  dependents_count?: number | null;
}

export interface Media extends Provenance {
  id: number;
  subject_kind: SubjectKind;
  subject_id: number;
  kind: MediaKind;
  storage_key: string | null;
  mime: string | null;
  byte_size: number | null;
  sha256: string | null;
  source_kind: MediaSource | null;
  source_url: string | null;
  fetched_at: string | null;
  status: MediaStatus;
  error: string | null;
}

// ═══ API shapes (BUILD-BRIEF §4) ════════════════════════════════════════════

/** The JSON error shape. Every route that says no says it like this. */
export interface ApiError {
  error: string;
}

/** Every list endpoint. `?q=` is a trigram search; `total` is the count BEFORE any limit. */
export interface ListResponse<T> {
  items: T[];
  total: number;
}

export interface OrganisationDetail {
  organisation: Organisation;
  people: Array<Person & { role_title: string | null; affiliation_status: AffiliationStatus }>;
  opportunities: OpportunityCard[];
  commitments: CommitmentView[];
}

export interface PersonDetail {
  person: Person;
  affiliations: AffiliationView[];
  facts: Fact[];
  commitments: CommitmentView[];
}

export interface OpportunityDetail {
  opportunity: OpportunityCard;
  events: OpportunityEvent[];
}

export interface PipelineSummary {
  /** Σ face_value × probability over the open stages. */
  weighted: number;
  face_value: number;
  counts: Record<Stage, number>;
  stale: number;
}

/** GET /api/proposals/:id — the queue's context before the operator commits. */
export interface ProposalDetail {
  proposal: Proposal;
  /**
   * What holding would collide with. For a `fact`: the current value of the same key. For a
   * `person`/`candidate`: trigram matches on the name. Empty means the write is clean.
   */
  collisions: Array<{ kind: string; ref?: string | null; label: string; detail?: string | null }>;
  /**
   * Whether the collision lane was actually consulted. FALSE means the check did not run, and
   * an empty `collisions` then means "nobody looked", not "the write is clean". A missing check
   * is never a passed check.
   */
  collisions_checked?: boolean;
  /**
   * Validation run over the payload before the write is offered. `warn` is something for the
   * operator to weigh, not a refusal: every check can warn and the hold still be right.
   */
  checks?: Array<{ level: 'pass' | 'warn'; code: string; detail?: string | null }>;
  /** Payload keys the applier needs and the proposal does not carry. */
  missing?: string[];
  /** The ref holding would mint, shown in the write preview. */
  next_ref?: string | null;
  subject_label?: string | null;
  /** A `dedupe` proposal only: both organisations, at full dress, so the operator can answer
   *  "are these the same" without leaving the queue. NULL when a side no longer exists. */
  pair?: DedupePair | null;
}

// ═══ organisation dedupe (P1: the finder proposes, the operator decides) ═══════

/** What holding a `dedupe` proposal does. The operator may amend it before holding. */
export type DedupeAction = 'merge' | 'relate' | 'dismiss';

/** Why the finder believes two rows are one organisation. A score alone is never a reason. */
export type DedupeReasonKind =
  | 'name_similarity' | 'shared_domain' | 'shared_uid'
  | 'shared_people' | 'alias_match' | 'punctuation_only';

export interface DedupeReason {
  kind: DedupeReasonKind;
  /** The evidence in words: the domain, the UID, the people's names, the two normalised forms. */
  detail: string | null;
}

/** The payload of a `dedupe` proposal, as the finder files it and the operator may amend it. */
export interface DedupePayload {
  action: DedupeAction;
  a_id: number;
  b_id: number;
  /** merge: the row that stays. relate: the parent. dismiss: carried along, unused. */
  survivor_id: number;
  /** The two sides as the finder read them, so the queue rail can name both without the
   *  pair. Display only: the applier reads the rows by id and never trusts these. */
  a_ref?: string;
  b_ref?: string;
  a_name?: string;
  b_name?: string;
  reasons: DedupeReason[];
  /** 0–1, the strongest reason's weight; the name similarity when that is what paired them. */
  score: number;
}

/**
 * One side of a pair as GET /api/proposals/:id draws it. `people` is the five most recent
 * current affiliations, `people_count` the whole number. Every count is computed, so none
 * carries a claim mark.
 */
export interface DedupeCard {
  id: number;
  ref: string;
  name: string;
  status: OrganisationStatus;
  merged_into: number | null;
  domains: string[];
  uid: string | null;
  canton: string | null;
  people_count: number;
  people: Array<{ name: string; ref: string; role_title: string | null }>;
  open_deals: number;
  facts_count: number;
  interactions_count: number;
  commitments_count: number;
  created_by_kind: CreatedByKind | null;
  created_at: string | null;
}

export interface DedupePair {
  a: DedupeCard;
  b: DedupeCard;
}

/**
 * One pair the finder would (or did) propose. `proposal_id`/`ref` are set when an unheld
 * `dedupe` proposal already stands for the pair — GET /api/dedupe/candidates is a dry run and
 * annotates rather than hides those, so the list reads the same before and after a run.
 */
export interface DedupeCandidate {
  a_id: number;
  b_id: number;
  a_ref: string;
  b_ref: string;
  a_name: string;
  b_name: string;
  action: DedupeAction;
  survivor_id: number;
  reasons: DedupeReason[];
  score: number;
  proposal_id: number | null;
  ref: string | null;
}

/** POST /api/dedupe/run — what the finder filed and what it left alone, in numbers. */
export interface DedupeRunResult {
  proposed: number;
  skipped_dismissed: number;
  /** Pairs whose earlier `dedupe` proposal an operator refused: a decision, not re-filed. */
  skipped_refused: number;
  skipped_pending: number;
  /** Pairs already linked parent ↔ child by a held relate: nothing left to propose. */
  skipped_related: number;
}

/** A held dismissal: the finder never proposes this pair again. `a_id < b_id` always. */
export interface DedupeDismissal {
  id: number;
  a_id: number;
  b_id: number;
  reason: string | null;
  proposal_id: number | null;
  decided_by: number | null;
  decided_at: string;
  a_ref?: string;
  b_ref?: string;
  a_name?: string;
  b_name?: string;
}

export interface ProposalSummary {
  unheld: number;
  by_kind: Partial<Record<ProposalKind, number>>;
  by_principal: Record<string, number>;
  /** ISO timestamp of the oldest unheld row — the queue's age, not its size. */
  oldest_unheld?: string | null;
  unheld_principals?: number;
  held_7d?: number;
  amended_7d?: number;
  refused_7d?: number;
  /** held + amended + refused over the window. */
  treated?: number;
  refused_by_reason_7d?: Partial<Record<RefuseReason, number>>;
  /** Mean edit distance between proposed and held payloads; null when nothing was amended. */
  edit_distance?: number | null;
  /** The run currently filing, when one is. */
  hot_run?: { run_id: number; principal: string; filed: number; refused: number; unheld: number; pushed_back: number } | null;
}

/**
 * GET /api/today — the briefing rail.
 *
 * A value is `null` when NOTHING PRODUCED IT YET (there is no signals monitor, no calendar
 * feed). The rail renders `null` as `fv--unknown` "not collected". It is never rendered as 0:
 * a zero is a counted fact and this is the absence of counting (B8 / ATTEST).
 */
export interface TodayResponse {
  unheld: number | null;
  signals: number | null;
  ball: { us: number; late: number; them: number } | null;
  stale: number | null;
  cooling: number | null;
  meetings_today: Array<{
    interaction_id: number;
    ref: string;
    title: string;
    occurred_at: string | null;
    organisation_name: string | null;
    people: string[];
  }>;
  ball_line: CommitmentView[];
  cooling_people: Array<{
    person_id: number;
    ref: string;
    name: string;
    tier: Tier;
    cadence_days: number;
    days_since: number | null;
    organisation_name: string | null;
  }>;
}

/** GET /api/surfe/health. */
export interface SurfeHealth {
  ok: boolean;
  credits?: { email: number | null; mobile: number | null; search: number | null };
  checked_at?: string;
  error?: string;
}

/**
 * GET /api/agents/health — one line per dependency, each honest about being unreachable.
 *
 * Round 2 adds six producers. Every one of them is OPTIONAL on the wire: a key that is absent
 * means the probe does not exist yet and the band renders `fv--unknown` "not collected"; a key
 * that is present with `ok: false` means the probe RAN and the thing is down, which is a
 * different fact and must look different (ATTEST: `unknown` is not `nil`).
 */
export interface AgentsHealth {
  litellm: { ok: boolean; base_url?: string | null; model?: string | null; error?: string | null };
  surfe: SurfeHealth;
  mail: MailHealth;
  db: { ok: boolean; error?: string | null; latency_ms?: number | null };
  /** The DeepSeek Harness (`dsh`) the runner spawns: DSH_BIN resolves, and which version against the pin. */
  harness?: { ok: boolean; bin?: string | null; pin?: string | null; version?: string | null; error?: string | null };
  /** The run loop itself: children alive, runs waiting, the gate's cap right now, and whether every skill file loads. */
  runtime?: { ok: boolean; active: number; queued: number; cap_now: number; night: boolean; skills_ok: boolean; error?: string | null };
  /** LITELLM_EMBEDDING_MODEL. `ok: false` with an error is the honest state while it is down. */
  embedding?: { ok: boolean; model?: string | null; dimensions?: number | null; error?: string | null };
  /** LITELLM_RERANKER_MODEL. Optional in the same way as embeddings. */
  reranker?: { ok: boolean; model?: string | null; error?: string | null };
  /** The scout process, seen through the queue: a worker that has not claimed recently is not ok. */
  scout?: {
    ok: boolean;
    worker?: string | null;
    last_claim_at?: string | null;
    queue_depth?: number | null;
    error?: string | null;
  };
  /** The local Hermes install (§4.4): detected read-only from $HERMES_HOME plus the ledger. */
  hermes?: {
    installed: boolean;
    home: string;
    version: string | null;
    config_has_crm: boolean;
    last_call_at: string | null;
    calls_24h: number;
    error?: string | null;
  };
}

/** The mail leg of the health band: which sender is configured, and whether inbound is live. */
export interface MailHealth {
  mode: 'stub' | 'smtp';
  ok?: boolean;
  error?: string | null;
  /** 'imap' when MAIL_IMAP_HOST is set and the connection is up, 'drop' when only the folder is. */
  inbound?: 'imap' | 'drop' | 'off' | null;
  last_sync_at?: string | null;
  drop_dir?: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// ROUND 2 — the intelligence layer (BUILD-BRIEF-R2 §3–§11).
//
// The same law as above: an interface here either mirrors a table in schema.sql or a response
// the brief documents. And the same reading of absence, everywhere:
//
//   null  = NOBODY PRODUCED IT. No monitor has run, no model was reachable, nothing counted.
//           The rail renders it `fv--unknown` "not collected".
//   0     = COUNTED, and the count is zero. The rail renders it `fv--nil`.
//
// They are never interchangeable. A producer that has not run returns null; a producer that
// ran and found nothing returns 0. Optional (`?`) fields carry the first meaning as well: the
// key is absent because that leg of the response has no producer yet.
// ═══════════════════════════════════════════════════════════════════════════

// ── §3.1 agent plumbing ────────────────────────────────────────────────────

/** The six scopes a capability can demand. `send` is deliberately not among them (§0). */
export type Scope = 'read' | 'propose' | 'scout:request' | 'surfe:search' | 'snapshot:write' | 'run:start';
export const SCOPES: Scope[] = ['read', 'propose', 'scout:request', 'surfe:search', 'snapshot:write', 'run:start'];

export type PrincipalKind = 'internal' | 'external' | 'worker' | 'system';
export type Transport = 'rest' | 'mcp' | 'stdio' | 'internal';
export type ToolOutcome = 'ok' | 'refused' | 'error';

/** A caller of the registry. `token_hash` never leaves the server; the clear-text bearer is
 *  shown once, at creation, and never again. */
export interface Principal {
  id: number;
  name: string;                       // hermes · claude-code · scout · signet · run:58
  kind: PrincipalKind;
  scopes: Scope[];
  /** First 8 characters of the bearer, so a human can recognise a token in a list. */
  token_prefix: string | null;
  /** Whether a bearer exists at all. The hash itself is never serialised. */
  has_token?: boolean;
  rate_per_minute: number;
  max_concurrency: number;
  expires_at: string | null;
  revoked_at: string | null;
  last_seen_at: string | null;
  created_at: string;
  created_by: number | null;
  /** Joined for the Principals panel: calls in the last 24 h. Absent = not counted. */
  calls_24h?: number;
}

/** The one-time answer to creating a principal. The token is never retrievable afterwards. */
export interface PrincipalCreated {
  principal: Principal;
  /** Shown once. If the operator loses it, the answer is a new token, not a lookup. */
  token: string;
}

/** One row of the ledger: who called what, with which outcome, however it ended. */
export interface ToolCall {
  id: number;
  principal_id: number | null;
  principal: string;
  run_id: number | null;
  transport: Transport;
  tool: string;
  /** Strings over 8 kB are truncated with a marker before they are stored. */
  input: Record<string, unknown> | null;
  outcome: ToolOutcome;
  error: string | null;
  latency_ms: number | null;
  idempotency_key: string | null;
  created_at: string;
}

export type RunStatus = 'queued' | 'running' | 'done' | 'failed' | 'aborted';
export type RunTrigger = 'operator' | 'schedule' | 'event' | 'agent';

/** One harness child, from spawn to finalize. `turns`, `tokens_in` and `tokens_out` are null for
 *  a harness run — dsh does not report them, and null reads "not collected", never zero. */
export interface Run {
  id: number;
  ref: string;                        // RUN-0058
  principal_id: number | null;
  skill: string;
  subject_kind: SubjectKind | null;
  subject_id: number | null;
  status: RunStatus;
  triggered_by: RunTrigger;
  model: string | null;
  started_at: string | null;
  ended_at: string | null;
  turns: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  wall_ms: number | null;
  transcript_path: string | null;
  summary: string | null;
  error: string | null;
  proposals_filed: number;
  created_at: string;
  created_by: number | null;
  /** Joined for the Runs zone. */
  subject_label?: string | null;
  subject_ref?: string | null;
}

/** The closed set of things a run can be seen doing (BUILD-BRIEF-R3f §1). `truncated` is the
 *  2 001st step of a run and the last one written. */
export type RunStepKind =
  | 'queued' | 'started' | 'plan' | 'search' | 'fetch' | 'read' | 'model' | 'propose' | 'drop'
  | 'refusal' | 'wait' | 'finish' | 'truncated';

/** One row of `run_steps` — the append-only account of a run, written by whichever loop drives
 *  it and read by the Run desk. `detail` strings are clipped at 2 kB with a marker. */
export interface RunStep {
  id: number;
  run_id: number;
  at: string;
  kind: RunStepKind;
  title: string;
  detail: Record<string, unknown> | null;
  tokens_in: number | null;
  tokens_out: number | null;
}

/** GET /api/runs/:id/steps?after=<id>&limit=<n> — ascending, `after` exclusive. */
export interface RunStepsPage {
  items: RunStep[];
  total: number;
  /** The id of the last item, or the `after` given when there are none — pass it back. */
  last_id: number;
}

/** What the one concurrency gate says about a queued run (`src/server/agents/concurrency.ts`). */
export interface RunGate {
  live: number;
  cap_now: number;
  night: boolean;
  /** A weekend day counted as night (the night cap in force all day). */
  weekend?: boolean;
  /** 1-based place in its loop's queue; null once the run is no longer queued. */
  position: number | null;
  /** Runs live on DeepSeek's API — the lane the GPU cap does not count (2026-09-04). */
  live_remote?: number;
  /** The remote lane's cap; null when the operator set none. */
  cap_remote?: number | null;
}

/** GET /api/runs/:id — the row plus what the transcript viewer needs. */
export interface RunDetail {
  run: Run;
  /** The ledger rows filed under `run:<id>`, newest last. */
  calls: ToolCall[];
  /** The proposals this run filed. */
  proposals: Array<Pick<Proposal, 'id' | 'ref' | 'kind' | 'status' | 'created_at'>>;
  /** The run's report — the harness's final answer (`answer.md`); null until the run has written it. */
  transcript: string | null;
  /** The gate's view of a QUEUED run; null or absent otherwise (R3f). */
  gate?: RunGate | null;
  /** How many `run_steps` rows the run has; absent on a server that predates R3f. */
  steps_total?: number;
}

/** A row of the cron table. `last_status` is free text so a job may say more than ok/failed;
 *  'no_handler' is the scheduler's own word for a job row nobody has registered a handler for. */
export interface Job {
  name: string;
  cron: string;
  enabled: boolean;
  last_run_at: string | null;
  last_status: string | null;
  last_error: string | null;
  next_run_at: string | null;
  running_since: string | null;
  runs_count: number;
  /** Whether a handler is registered in THIS process. False means the row exists and nothing
   *  will run it — an honest state, not a silent skip. */
  registered?: boolean;
}

/** Every event kind the app emits. A consumer that does not know a kind ignores it. */
export type EventKind =
  | 'interaction.created'
  | 'commitment.created' | 'commitment.discharged'
  | 'proposal.held' | 'proposal.refused'
  | 'signal.created'
  | 'mail.received' | 'mail.sent'
  | 'opportunity.stage'
  | 'research.dossier_written'
  | 'journey.step_due' | 'journey.replied'
  | 'score.changed'
  | 'organisation.merged'
  /** Two rows, one human: `services/personMerge.ts` emitted this inside the merge's own
   *  transaction. The OLD events naming the merged-away row are never re-pointed — `events` is
   *  append-only history and a row says what was true when it was written (P4). */
  | 'person.merged';

/** Append-only. There is no update route and no delete route for this table. */
export interface Event {
  id: number;
  kind: EventKind | string;
  subject_kind: string | null;
  subject_id: number | null;
  payload: Record<string, unknown>;
  principal: string | null;
  created_at: string;
}

export interface EventCursor {
  consumer: string;
  last_event_id: number;
  updated_at: string;
}

/** One before/after of a setting, and the proposal that asked for the change. */
export interface SettingsHistoryEntry {
  id: number;
  key: string;
  old_value: string | null;
  new_value: string | null;
  proposal_id: number | null;
  changed_by: number | null;
  changed_at: string;
}

// ── §3.2 scout and snapshots ───────────────────────────────────────────────

/** Round 3 adds `pdf`: fetch a PDF and return its extracted text (BUILD-BRIEF-R3 §6). Round 7
 *  adds `directory`: one tel.search.ch firma lookup, for an organisation's switchboard and never
 *  for a person (BUILD-BRIEF-R7 §5.3). Kept equal to `scout/types.ts#JobKind` by hand. */
export type ScoutJobKind = 'snapshot' | 'search' | 'probe' | 'pdf' | 'document' | 'directory';
export type ScoutJobStatus = 'queued' | 'claimed' | 'done' | 'failed' | 'refused';

export interface ScoutJob {
  id: number;
  kind: ScoutJobKind;
  input: Record<string, unknown>;
  priority: number;
  status: ScoutJobStatus;
  claimed_by: string | null;
  claimed_at: string | null;
  finished_at: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
  attempts: number;
  requested_by: string | null;
  run_id: number | null;
  purpose: string | null;
  created_at: string;
}

/** What a `snapshot` job returns before the files are stored. */
export interface SnapshotResult {
  url: string;
  final_url: string;
  status_code: number;
  redirect_chain: Array<{ url: string; status: number }>;
  title: string | null;
  html_sha256: string;
  text_sha256: string;
  byte_size: number;
  robots_allowed: boolean;
}

/** What a `search` job returns. `cost_units` is what the provider charged us, 0 for SearXNG. */
export interface ScoutSearchResult {
  provider: 'searxng' | 'brave';
  results: Array<{ url: string; title: string; snippet: string; age?: string | null; extra?: Record<string, unknown> }>;
  cost_units: number;
}

/**
 * A page as the scout saw it. The bytes live beside the row under
 * `data/snapshots/<yyyy>/<mm>/<id>.{html,txt,png}`; the text file is the ONE thing a quote is
 * checked against, character for character.
 */
export interface Snapshot {
  id: number;
  url: string;
  final_url: string | null;
  status_code: number | null;
  redirect_chain: Array<{ url: string; status: number }> | null;
  fetched_at: string | null;
  title: string | null;
  html_path: string | null;
  text_path: string | null;
  png_path: string | null;
  html_sha256: string | null;
  text_sha256: string | null;
  byte_size: number | null;
  /** null = robots was never consulted (a refusal for another reason, or a local fixture). */
  robots_allowed: boolean | null;
  refused_reason: string | null;
  job_id: number | null;
  requested_by: string | null;
  run_id: number | null;
  /** The PDF the scout's pdf job kept beside the text, as the scout wrote it; null for a page. */
  pdf_path?: string | null;
  /** The bytes a `document` job kept (a workbook, a JSON/CSV/XML file); a kept PDF fills this too. */
  file_path?: string | null;
  /** The kept file's content type, as sniffed by the scout; null when nothing was kept. */
  content_type?: string | null;
  created_at: string;
}

/** GET /api/scout/health — the worker seen through the queue, never through a self-report. */
export interface ScoutHealth {
  ok: boolean;
  worker: string | null;
  last_claim_at: string | null;
  queue_depth: number;
  failed_24h: number;
  refused_24h: number;
  error?: string | null;
}

// ── §3.3 the personal search index ─────────────────────────────────────────

export type SearchProvider = 'brave' | 'searxng' | 'snapshot' | 'index';
/** Where the answer actually came from. 'unavailable' is NOT an empty result: it means no
 *  worker and no provider answered, and the page must say so in words. */
export type ServedFrom = 'index' | 'index_near' | 'provider' | 'unavailable';
export type ResultOrigin = 'searxng' | 'snapshot' | 'brave';

export interface Search {
  id: number;
  query_raw: string;
  query_norm: string;
  country: string;
  lang: string | null;
  purpose: string | null;
  provider: SearchProvider | null;
  served_from: ServedFrom | null;
  requested_by: string | null;
  run_id: number | null;
  result_count: number | null;
  cost_units: number;
  fetched_at: string | null;
  ttl_until: string | null;
  created_at: string;
  /** Whether an embedding exists for this query. The vector itself never crosses the wire. */
  embedded?: boolean;
}

export interface SearchResult {
  id: number;
  url_norm: string;
  url: string;
  domain: string | null;
  title: string | null;
  snippet: string | null;
  origin: ResultOrigin;
  first_seen: string;
  last_seen: string;
  times_seen: number;
  snapshot_id: number | null;
  /** Brave-originated rows carry first_seen + brave_retention_hours; the rest carry
   *  last_seen + search.retention_days (BUILD-BRIEF-R2 §6). */
  retain_until: string;
  /** Joined from search_hits when the row arrives as part of one search's answer. */
  rank?: number;
  extra?: Record<string, unknown> | null;
}

/** GET /api/search?q= — and the return of the `search_web` capability. */
export interface SearchResponse {
  query: string;
  query_norm: string;
  served_from: ServedFrom;
  provider: SearchProvider | null;
  results: SearchResult[];
  /** The `searches` row this answer was recorded as. */
  search_id: number | null;
  cost_units: number;
  ttl_until: string | null;
  /** Set when `served_from` is 'unavailable' — what stopped us, in words the operator can act on. */
  unavailable_reason?: string | null;
}

/** GET /api/search/stats — the rail of SearchPage. A null is "not counted", never zero. */
export interface SearchStats {
  indexed_queries: number;
  results: number;
  domains: number;
  /** The 30-day split. Every key counted; absent keys are zero, not unknown. */
  served_from_30d: Record<ServedFrom, number>;
  /** Provider cost over 30 days, in the providers' own units. */
  cost_30d: number;
  /** null when the embedding endpoint has never been reachable, so nothing was ever embedded. */
  embedded: number | null;
  embedding_total?: number | null;
  /** The honest state of the embedding model behind the coverage figure. */
  embedding_ok?: boolean;
  embedding_error?: string | null;
}

// ── §3.4 mail ──────────────────────────────────────────────────────────────

export type MailDirection = 'in' | 'out';
export type MailSource = 'imap' | 'drop' | 'sent';
export type MailTriageStatus = 'linked' | 'unknown' | 'ignored' | 'self';
export type TriageStatus = 'open' | 'created' | 'attached' | 'ignored';
export type OutboxStatus = 'approved' | 'written' | 'sent' | 'failed';

export interface MailAttachment {
  filename: string | null;
  content_type: string | null;
  byte_size: number | null;
  sha256?: string | null;
  path?: string | null;
}

export interface MailMessage {
  id: number;
  message_id: string;
  in_reply_to: string | null;
  references_ids: string[];
  /** The thread root's message id — References[0], else the message's own. */
  thread_key: string | null;
  direction: MailDirection;
  from_addr: string | null;
  from_name: string | null;
  to_addrs: string[];
  cc_addrs: string[];
  subject: string | null;
  sent_at: string | null;
  text_body: string | null;
  html_path: string | null;
  raw_path: string | null;
  sha256: string | null;
  has_attachments: boolean;
  attachments: MailAttachment[];
  uid: number | null;
  uidvalidity: number | null;
  folder: string | null;
  source: MailSource;
  interaction_id: number | null;
  triage_status: MailTriageStatus | null;
  created_at: string;
}

/** One thread as the Inbox lists it. */
export interface MailThread {
  thread_key: string;
  subject: string | null;
  message_count: number;
  last_at: string | null;
  last_direction: MailDirection | null;
  /** The last message's first line, rendered in serif as the record's own words. */
  last_line: string | null;
  person_id: number | null;
  person_name: string | null;
  person_ref: string | null;
  organisation_name: string | null;
  /** Days since the last inbound message went unanswered; null when nothing is waiting. */
  waiting_days: number | null;
}

/** An address nobody on the record owns, waiting for the operator's judgement. */
export interface MailTriage {
  id: number;
  address: string;
  display_name: string | null;
  domain: string | null;
  first_seen: string;
  last_seen: string;
  message_count: number;
  organisation_match_id: number | null;
  status: TriageStatus;
  resolved_person_id: number | null;
  resolved_at: string | null;
  resolved_by: number | null;
  /** Joined for the row. */
  organisation_name?: string | null;
  organisation_ref?: string | null;
}

/** A message an operator approved. Nothing writes `sent` except an operator click. */
export interface OutboxItem {
  id: number;
  proposal_id: number | null;
  to_addrs: string[];
  cc_addrs: string[];
  subject: string | null;
  text_body: string | null;
  html_body: string | null;
  in_reply_to: string | null;
  status: OutboxStatus;
  eml_path: string | null;
  message_id: string | null;
  error: string | null;
  sent_at: string | null;
  sent_by: number | null;
  enrolment_id: number | null;
  experiment_variant: string | null;
  created_at: string;
  /** Joined for the Drafts zone. */
  person_name?: string | null;
  person_ref?: string | null;
  proposal_ref?: string | null;
}

/**
 * GET /api/mail/profile/:personId — how this person and the operator actually write to each
 * other. EVERY field is null when there is not enough evidence (fewer than two messages);
 * the component renders that as `fv--unknown` "not collected" and never as zero.
 */
export interface WritingProfile {
  person_id: number;
  messages_in: number;
  messages_out: number;
  /** Median hours to reply. null = never measured (no reply pair in the record). */
  median_reply_hours_ours: number | null;
  median_reply_hours_theirs: number | null;
  median_words_ours: number | null;
  median_words_theirs: number | null;
  /** Verbatim, exactly as written — never normalised, never title-cased. */
  greeting: string | null;
  sign_off: string | null;
  /** The register the evidence supports, with how many lines support it. */
  register: Register | null;
  register_evidence: number | null;
  language: PersonLanguage | null;
  /** True when fewer than two messages exist — the whole profile is then unknown, not empty. */
  insufficient: boolean;
}

/** GET /api/mail/summary — the Inbox rail and the sidebar's `inbox.needs_me` count. */
export interface MailSummary {
  /** Threads whose last message is inbound and unanswered. */
  needs_me: number | null;
  unknown_senders: number | null;
  drafts: number | null;
  outbox: number | null;
  threads_7d: number | null;
}

// ── §3.5 monitors and signals ──────────────────────────────────────────────

export type MonitorKind =
  | 'website' | 'careers' | 'news' | 'shab' | 'zefix' | 'surfe_job' | 'tender' | 'budget' | 'linkedin_manual'
  // round 3 — population and financial sources (BUILD-BRIEF-R3 §6)
  | 'directory' | 'zewo' | 'accreditation' | 'dataset' | 'annual_report' | 'lindas';
export type MonitorStatus = 'active' | 'paused' | 'failed';
export type MonitorOutcome = 'unchanged' | 'changed' | 'signal' | 'error' | 'refused';

export interface Monitor extends Provenance {
  id: number;
  subject_kind: SubjectKind | null;
  subject_id: number | null;
  kind: MonitorKind;
  target: string | null;
  cadence_hours: number;
  last_run_at: string | null;
  next_run_at: string | null;
  last_snapshot_id: number | null;
  last_text_sha256: string | null;
  status: MonitorStatus;
  last_error: string | null;
  /** Joined for the Monitors table. */
  subject_label?: string | null;
  subject_ref?: string | null;
  last_outcome?: MonitorOutcome | null;
}

/** Append-only. A refusal (robots, no credentials) is RECORDED as an outcome, never skipped. */
export interface MonitorRun {
  id: number;
  monitor_id: number;
  started_at: string;
  ended_at: string | null;
  outcome: MonitorOutcome | null;
  snapshot_id: number | null;
  /** Changed words / total words. null when nothing was diffed. */
  diff_magnitude: number | null;
  detail: Record<string, unknown> | null;
}

/** A one-click monitor the operator may enable, shipped by services/monitors/presets.ts. */
export interface MonitorPreset {
  id: string;
  kind: MonitorKind;
  target: string;
  label: string;
  note: string;
  cadence_hours: number;
  /** True when a monitor with this target already exists — the button then says so. */
  enabled?: boolean;
}

export type SignalKind = 'risk' | 'budget' | 'board' | 'move' | 'post' | 'tender' | 'mention' | 'coverage';
export type SignalSeverity = 'info' | 'attend' | 'high';
export type SignalStatus = 'new' | 'seen' | 'acted' | 'dismissed';
export type SignalDisposition = 'none' | 'task' | 'draft' | 'dismissed';
export type SignalEvidenceKind = 'snapshot' | 'publication' | 'search' | 'surfe' | 'manual' | 'computed';

export interface Signal extends Provenance {
  id: number;
  ref: string;                        // SIG-0007
  kind: SignalKind;
  severity: SignalSeverity;
  subject_kind: SubjectKind | null;
  subject_id: number | null;
  headline: string;
  detail: string | null;
  evidence_kind: SignalEvidenceKind | null;
  evidence_ref: string | null;
  /** Verbatim, from the snapshot text or the publication title. Never paraphrased (P2). */
  quote: string | null;
  monitor_id: number | null;
  first_seen: string;
  last_seen: string;
  status: SignalStatus;
  disposition: SignalDisposition;
  /** What the disposition wrote: a COM- ref for a task, a PRO- ref for a draft. */
  disposition_ref: string | null;
  principal: string | null;
  /** Joined for the feed. */
  subject_label?: string | null;
  subject_ref?: string | null;
  snapshot_id?: number | null;
}

// ── §3.6 research ──────────────────────────────────────────────────────────
//
// A research is a `deep_research` run (`Run`, skill 'deep_research'); its report is the run's
// `summary`. There is no per-subject cadence and no depth (BUILD-BRIEF-R4).

/** One numbered citation in a dossier. The quote MUST be a literal substring of the snapshot's
 *  text file — `write_dossier` refuses the whole document otherwise. */
export interface Citation {
  n: number;
  snapshot_id: number;
  url: string;
  quote: string;
  fetched_at?: string | null;
}

/** A document, not a fact. It is never held, so it keeps its ◇ mark for ever. */
export interface Dossier {
  id: number;
  ref: string;                        // DOS-0003
  subject_kind: SubjectKind | null;
  subject_id: number | null;
  question: string | null;
  version: number;
  title: string | null;
  body_md: string | null;
  citations: Citation[];
  run_id: number | null;
  principal: string | null;
  supersedes_id: number | null;
  created_at: string;
  /** How many versions exist for this subject, this one included. */
  version_count?: number;
}

// ── §3.7 buying groups · scoring · journeys · personas ─────────────────────

export type BuyingRole =
  | 'sponsor' | 'budget_owner' | 'it_owner' | 'user' | 'procurement' | 'data_protection' | 'other';
export type Stance = 'unknown' | 'champion' | 'neutral' | 'blocker';

/** The six roles coverage is measured against. `other` is a label, not a role to cover. */
export const BUYING_ROLES: BuyingRole[] = [
  'sponsor', 'budget_owner', 'it_owner', 'user', 'procurement', 'data_protection',
];

export interface BuyingGroupMember {
  id: number;
  opportunity_id: number;
  person_id: number;
  role: BuyingRole;
  stance: Stance;
  note: string | null;
  created_at: string;
  created_by: number | null;
  person_name?: string | null;
  person_ref?: string | null;
  /** Days since the last interaction with this person; null when there has never been one. */
  last_contact_days?: number | null;
}

/** Arithmetic over the six roles — a computed count, so it carries no claim mark. */
export interface Coverage {
  opportunity_id: number;
  roles_total: number;
  roles_filled: number;
  missing: BuyingRole[];
  per_role: Partial<Record<BuyingRole, { person_id: number; person_name: string; last_contact_days: number | null } | null>>;
}

/** One rule that fired, with the sentence that says why — composed from data, never generic. */
export interface ScoreReason {
  rule: string;
  points: number;
  because: string;
}

export interface Score {
  person_id: number;
  score: number;
  breakdown: ScoreReason[];
  computed_at: string;
  person_name?: string | null;
  person_ref?: string | null;
}

/** The rule document in app_settings['scoring.rules']. Weights change only through a
 *  `rule_change` proposal (BUILD-BRIEF-R2 §10). */
export interface ScoringRules {
  version: number;
  rules: Array<{ id: string; points: number; when?: string }>;
}

export type ActionKind = 'send' | 'call' | 'meet' | 'research' | 'introduce' | 'wait';
export type ActionStatus = 'open' | 'done' | 'dismissed' | 'expired';

/** One next best action. `why_now_by` is 'rules' when the rule text stands, or `run:<id>` when
 *  the `why_now` skill rewrote it — in which case it is a claim and carries ◇. */
export interface Action {
  id: number;
  person_id: number;
  kind: ActionKind;
  title: string | null;
  why_now: string | null;
  why_now_by: string | null;
  runners_up: Array<{ kind: ActionKind; why_now: string; score?: number }>;
  status: ActionStatus;
  expires_on: string | null;
  run_id: number | null;
  done_ref: string | null;
  created_at: string;
  person_name?: string | null;
  person_ref?: string | null;
  score?: number | null;
}

export type SequenceStatus = 'draft' | 'running' | 'paused' | 'ended';
export type BranchOn = 'reply' | 'no_reply' | `signal:${SignalKind}`;

/** Where a step goes next. `goto` is a step number or the end of the sequence. */
export interface Branch {
  on: BranchOn;
  goto: number | 'end';
}

export interface Step {
  n: number;
  wait_days: number;
  channel: 'mail';
  template_md: string;
  subject: string;
  branches: Branch[];
}

export interface Sequence {
  id: number;
  ref: string;                        // SEQ-0002
  name: string;
  status: SequenceStatus;
  steps: Step[];
  daily_cap: number;
  segment_persona_id: number | null;
  created_at: string;
  created_by: number | null;
  /** Joined for the table. Absent = not counted. */
  enrolled?: number;
  active?: number;
  replied?: number;
}

export type EnrolmentStatus = 'active' | 'paused' | 'replied' | 'completed' | 'stopped' | 'suppressed';

export interface Enrolment {
  id: number;
  sequence_id: number;
  person_id: number;
  step_n: number;
  status: EnrolmentStatus;
  next_at: string | null;
  last_event_id: number | null;
  enrolled_at: string;
  enrolled_by: number | null;
  person_name?: string | null;
  person_ref?: string | null;
  /** Why the enrolment was suppressed, in words. Only set when status is 'suppressed'. */
  suppressed_reason?: string | null;
}

export type ExperimentVariable = 'hook' | 'length' | 'tone' | 'timing';
export type ExperimentOutcome = 'pending' | 'replied' | 'no_reply' | 'meeting';

export interface Experiment {
  id: number;
  name: string;
  variable: ExperimentVariable;
  variants: Array<{ key: string; label?: string; value?: string }>;
  sequence_id: number | null;
  started_at: string | null;
  ended_at: string | null;
}

export interface ExperimentAssignment {
  proposal_id: number;
  experiment_id: number;
  variant: string;
  outbox_id: number | null;
  outcome: ExperimentOutcome;
  outcome_at: string | null;
}

/**
 * Attribution per variant. `reply_rate` and `meeting_rate` are null until n ≥ 20 — below that
 * the page says "too few to say" rather than printing a percentage nobody should believe.
 */
export interface ExperimentReport {
  experiment_id: number;
  variable: ExperimentVariable;
  variants: Array<{
    variant: string;
    n: number;
    replies: number;
    meetings: number;
    reply_rate: number | null;
    meeting_rate: number | null;
  }>;
  /** True while every variant is under the threshold — the whole report is then unreadable. */
  too_few: boolean;
}

export type PersonaSubjectKind = 'person' | 'segment';
export type PersonaStatus = 'proposed' | 'held' | 'superseded';
export type TraitKey = 'topics' | 'style' | 'decision_role' | 'objections' | 'register' | 'timing';

/** A trait without at least one verbatim quote is dropped before the proposal is filed. */
export interface Trait {
  key: TraitKey | string;
  value: string;
  evidence: Evidence[];
}

export interface Persona extends Provenance {
  id: number;
  subject_kind: PersonaSubjectKind;
  subject_id: number | null;
  name: string | null;
  traits: Trait[];
  version: number;
  /** A person persona arrives 'proposed' and is held by a click; a segment persona is the
   *  operator's own form and is written 'held'. */
  status: PersonaStatus;
  run_id: number | null;
  principal: string | null;
  subject_label?: string | null;
  subject_ref?: string | null;
}

/** The operator's segment form, before it becomes a `personas` row. */
export interface SegmentPersonaInput {
  name: string;
  titles: string[];
  seniorities: string[];
  departments: string[];
  sectors: string[];
  cantons: string[];
  topics: string[];
}

// ── §11 Signet handover ────────────────────────────────────────────────────

/**
 * The payload POSTed to Signet, or written to `data/outbox/signet/<OPP-ref>.json` by the stub.
 * Every crm_* field is the join key on the Signet side; the handover is idempotent on
 * `engagement.crm_opp_ref`.
 */
export interface SignetHandoverPayload {
  client: {
    name: string;
    legal_name: string | null;
    sector: string | null;
    address: string | null;
    language: PersonLanguage | null;
    crm_org_id: number;
    crm_org_ref: string;
  };
  contacts: Array<{
    first_name: string | null;
    last_name: string | null;
    role_title: string | null;
    email: string | null;
    is_sponsor: boolean;
    crm_person_ref: string;
  }>;
  engagement: {
    name: string;
    estimate_value: string | null;
    currency: string;
    kickoff_date: string | null;
    language: PersonLanguage | null;
    crm_opp_ref: string;
  };
}

export type HandoverStatus = 'none' | 'pending' | 'done' | 'failed';

/** POST /api/signet/handover/:opportunityId. `mode` says which door was actually used. */
export interface SignetHandoverResult {
  mode: 'live' | 'stub';
  status: HandoverStatus;
  payload: SignetHandoverPayload;
  /** Set on the live path only. */
  client_id?: number | null;
  engagement_id?: number | null;
  code?: string | null;
  /** Set on the stub path: where the payload was written. */
  path?: string | null;
  error?: string | null;
}

/** GET /api/signet/health — configured, or honestly a stub. */
export interface SignetHealth {
  ok: boolean;
  mode: 'live' | 'stub';
  base_url?: string | null;
  error?: string | null;
  pending?: number | null;
}

// ═══ ROUND 3 — the organisation raster, the market and the target score ═════
//
// The raster's own vocabulary (blocks, cells, values, the parser, the labels) lives in
// `src/shared/raster.v1.ts`, not here: it is a versioned document, and a copy of its values in
// this file would be a second source of truth that drifts. What follows is only the WIRE — the
// row types the two round-3 tables carry and the shapes the three round-3 routes answer.

// ── §2 the two computed tables ─────────────────────────────────────────────

/** The eight cells of the computed `triggers` block (BUILD-BRIEF-R3 §1). */
export type OrgTriggerKey =
  | 'leadership_change_12m' | 'new_strategy_period' | 'merger_12m' | 'regulatory_deadline_12m'
  | 'tender_12m' | 'digital_hiring_12m' | 'funding_shift_12m' | 'crisis_news_12m';

/**
 * The triggers block as `profile.compute` wrote it. Inside a row every trigger is a real
 * boolean; the ABSENCE of the row is the "nobody computed this yet" state, and a page must
 * render that as `fv--unknown`, never as eight falses.
 */
export type OrgTriggers = Record<OrgTriggerKey, boolean>;

/** How much of the raster this organisation actually holds. Arithmetic — it carries no mark. */
export interface OrgCoverage {
  /** Comparable cells outside the computed block: what COULD be filled. */
  defined: number;
  filled: number;
  /** filled / defined as a whole percent; 0 when nothing is defined, never NaN. */
  pct: number;
}

/** A row of `org_profiles_computed`. */
export interface OrgProfileComputed {
  organisation_id: number;
  triggers: OrgTriggers;
  coverage_defined: number;
  coverage_filled: number;
  computed_at: string;
}

export type OrgTier = 'target' | 'watch' | 'park' | 'exclude';
export type IcpAxisName = 'fit' | 'timing' | 'access';

/** One rule that fired, with the sentence composed FROM DATA that says why. */
export interface OrgScoreReason {
  axis: IcpAxisName;
  rule: string;
  points: number;
  because: string;
}

/** A row of `org_scores`. `composite = fit × timing × access`, 1–125. */
export interface OrgScore {
  organisation_id: number;
  fit: number;
  timing: number;
  access: number;
  composite: number;
  tier: OrgTier;
  breakdown: OrgScoreReason[];
  computed_at: string;
  /** Joined for the Market grid. */
  organisation_name?: string | null;
  organisation_ref?: string | null;
}

/**
 * The rule document in `app_settings['icp.rules']` (BUILD-BRIEF-R3 §5). Like `scoring.rules` it
 * changes ONLY through a held `rule_change` proposal — there is no PATCH and no setter.
 *
 * `reference_subsectors` ships EMPTY: the fit rule that reads it simply does not fire until
 * Signet's client sectors are configured or the operator names their own list. An empty list is
 * an honest "nobody said yet", never a guess at what the operator sells to.
 */
export interface IcpRule {
  id: string;
  /** Points added when the rule fires. Absent on a rule that clamps rather than adds. */
  points?: number;
  /** A rule that forces the whole axis to this value (do_not_contact → 1, open deal → 5). */
  set_to?: number;
  /** The sentence a reader checks the rule against. Not evaluated — the code is. */
  when?: string;
}

export interface IcpAxis {
  /** What the axis scores when nothing fires. */
  floor: number;
  rules: IcpRule[];
}

export interface IcpRules {
  version: number;
  axes: Record<IcpAxisName, IcpAxis>;
  /** Sub-sector values (raster `identity.subsector`) that count as the reference set. */
  reference_subsectors: string[];
  /**
   * The revenue below which a `public_law` body still scores the fit half-point. NULL until the
   * operator sets it — the rule then cannot fire, which is the honest state, not a zero.
   */
  public_law_revenue_threshold_chf: number | null;
  /** The months the timing axis treats as budget season (March–June). */
  spring_months: number[];
  /** Read top-down: the first band whose `min` the composite reaches. */
  tiers: Array<{ tier: OrgTier; min: number }>;
}

// ── §6 population sources ──────────────────────────────────────────────────

/** A federation member list. `members_seen` is NULL until an import counted — never 0. */
export interface Directory extends Provenance {
  id: number;
  name: string;
  url: string | null;
  federation: string | null;
  monitor_id: number | null;
  last_import_at: string | null;
  members_seen: number | null;
  /** Joined for the Population zone. */
  monitor_status?: MonitorStatus | null;
  monitor_kind?: MonitorKind | null;
  last_outcome?: MonitorOutcome | null;
  /** Unknown names from this list still sitting in the review queue as `organisation` proposals. */
  pending_organisations?: number | null;
}

// ── §4 the profile of one organisation ─────────────────────────────────────

/**
 * One raster cell as the record holds it. `value === null` is "not collected" and must render
 * as such — never as a zero, never as an empty string.
 */
export interface ProfileCellValue {
  /** `profile.<block>.<cell>` */
  key: string;
  block: string;
  cell: string;
  /** The canonical text `parseCell` produced, exactly as `facts.value` stores it. */
  value: string | null;
  /** How exact a numeric value is — `≥` / `≤` / `≈` in front of the figure; null = exact. */
  qualifier?: 'at_least' | 'at_most' | 'approx' | null;
  /** For a reference cell: the ref and name of the person or organisation the value points at
   *  ("PER-0122 · Ingrid Artieda"), so the grid never shows a bare id. */
  value_label?: string | null;
  method: FactMethod | null;
  confidence: number | null;
  /** True once an operator confirmed the fact: ▪ rather than ◇. */
  held: boolean;
  fact_id: number | null;
  as_of: string | null;
  evidence_kind: EvidenceKind | null;
  evidence_ref: string | null;
  quote: string | null;
  created_by_kind: CreatedByKind | null;
}

/** One organisation's current raster, keyed by the full cell key. */
export type RasterProfile = Record<string, ProfileCellValue>;

export interface ProfileBlockView {
  block: string;
  /** Every cell of the block, in raster order — including the ones nobody collected. */
  cells: ProfileCellValue[];
  coverage: OrgCoverage;
}

/** A PDF kept on the record for this organisation (the Documents zone): what `request_snapshot`
 *  keeps for a run on an organisation (BUILD-BRIEF-R4 §6 b). */
export interface ProfileDocument {
  snapshot_id: number;
  url: string;
  /** The report's year, when the file name or the URL names it — not the fetch year. */
  year: number | null;
  fetched_at: string | null;
  byte_size: number | null;
  /** Full cell keys whose evidence is this file: current facts and unheld proposals. */
  cited_cells: string[];
}

/**
 * The last `deep_research` run over this organisation, as the Profile head reads it
 * (BUILD-BRIEF-R4 §4): which run, when it ended, and how many raster cells it filed. The count
 * is arithmetic over that run's `fact` proposals on `profile.*` keys — a COMPUTED count, so it
 * carries no claim mark. NULL means no run has ever been started, which is not "zero cells".
 */
export interface ProfileLastResearch {
  run_id: number;
  run_ref: string;
  /** When the run ended; NULL while it is still queued or running. */
  at: string | null;
  /** `fact` proposals on `profile.*` keys this run filed, held or not. */
  cells_filed: number;
}

/** GET /api/organisations/:id/profile */
export interface ProfileResponse {
  organisation_id: number;
  organisation_ref: string;
  organisation_name: string;
  raster_version: string;
  blocks: ProfileBlockView[];
  coverage: OrgCoverage;
  /** NULL until `profile.compute` has run for this organisation. Not a row of falses. */
  computed: OrgProfileComputed | null;
  /** NULL until it has been scored. Not a composite of 1. */
  score: OrgScore | null;
  /** How many cells a run may propose — the "of n cells" figure. */
  proposable: number;
  /** NULL until a `deep_research` run has been started on this organisation. Not a row of zeros. */
  last_research: ProfileLastResearch | null;
  /** The PDFs kept for this organisation, newest first; empty is a counted zero. */
  documents: ProfileDocument[];
}

// ── §4 the market grid ─────────────────────────────────────────────────────

/** One row of the Market grid. Every nullable field is "not collected", never zero. */
export interface MarketRow {
  organisation_id: number;
  ref: string;
  name: string;
  /** The `identity.subsector` cell, already split into its values. */
  subsector: string[];
  canton: string | null;
  fte_band: string | null;
  budget_band: string | null;
  procurement_regime: string | null;
  fit: number | null;
  timing: number | null;
  access: number | null;
  composite: number | null;
  tier: OrgTier | null;
  coverage_pct: number | null;
  triggers_30d: number | null;
}

/** The Market rail. A null median is "nothing profiled yet", which is not a median of 0. */
export interface MarketAggregates {
  profiled: number;
  coverage_median_pct: number | null;
  targets: number;
  subsectors: number;
  triggers_30d: number;
  by_tier: Record<OrgTier, number>;
}

/** GET /api/market */
export interface MarketResponse {
  items: MarketRow[];
  total: number;
  aggregates: MarketAggregates;
}

/** One row of the Compare sheet: the same cell as both organisations hold it. */
export interface CompareCell {
  key: string;
  block: string;
  cell: string;
  a: ProfileCellValue | null;
  b: ProfileCellValue | null;
  /** True when both hold a value and the two texts differ — the row worth reading. */
  differs: boolean;
}

export interface CompareSide {
  organisation_id: number;
  ref: string;
  name: string;
  coverage: OrgCoverage;
  score: OrgScore | null;
}

/** GET /api/market/compare?a=&b= */
export interface CompareResponse {
  a: CompareSide;
  b: CompareSide;
  blocks: Array<{ block: string; cells: CompareCell[] }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// ROUND 3b — units, websites, the run group hold (BUILD-BRIEF-R3b). The harvest and discover
// loops went with BUILD-BRIEF-R4: a `deep_research` run proposes the people and the website.
// Every figure below is arithmetic and carries no claim mark; a null is "not counted", a zero
// is "counted and none".
// ═══════════════════════════════════════════════════════════════════════════

/** A `domain` proposal's payload (§2c). `source` says which producer found the host:
 *  `research` is a `deep_research` run that confirmed the name on the homepage (BUILD-BRIEF-R4
 *  §6 b), `directory` a member list's outward link, `zewo` the ZEWO detail page, `search` a
 *  search hit an agent confirmed. */
export interface DomainPayload {
  organisation_id: number;
  domain: string;
  source: 'research' | 'search' | 'directory' | 'zewo';
}

/** One node of an organisation's tree (§2b): the root or a unit, with computed figures. */
export interface OrganisationTreeNode {
  id: number;
  ref: string;
  name: string;
  parent_id: number | null;
  /** Current affiliations (held or proposed) at this node alone. */
  people: number;
  open_deals: number;
  /** The latest interaction at this node, as a date; null when none is on record. */
  last_contact_on: string | null;
  /** 0 for the root, 1 for a direct unit, at most 3. */
  depth: number;
}

/** GET /api/organisations/:id/tree */
export interface OrganisationTree {
  root: OrganisationTreeNode & {
    /** The root's own line of parents, outermost first — the breadcrumb ("OSEO Vaud › PRIMA"). */
    ancestors: Array<{ id: number; ref: string; name: string }>;
  };
  /** Every unit below the root, depth-first, depth ≤ 3. */
  units: OrganisationTreeNode[];
  /** People across the root and every unit (distinct persons). */
  people_in_tree: number;
  units_count: number;
}

/**
 * Where a person came from (§2b, the Origin line): the earliest interaction they took part in,
 * or — when no interaction exists — the import or the agent that put them on the record.
 */
export interface Origin {
  kind: 'interaction' | 'import' | 'agent' | 'operator';
  /** ISO date (YYYY-MM-DD) of the interaction, or of the row's creation. */
  date: string | null;
  /** The interaction's title, when kind is 'interaction'. */
  title: string | null;
  /** INT- ref of that interaction. */
  ref: string | null;
  /** 'import:seed', 'run:58' — the principal that filed the row, when one is known. */
  principal: string | null;
  /** The line as the mast prints it: "Atelier gouvernance · 15.07.2026" or "import · import:seed · 12.08.2026". */
  label: string;
}

// round 5 — the network vocabulary and its row/response shapes live in `network.v1.ts` (loaded
// by both sides, like `raster.v1.ts`); re-exported here so a caller of `shared/types.ts` finds
// them without a second import path.
export type {
  LinkRow, LinkPayload, LinkGhost, LinkMix, MixRow, OrganisationLinks, NetworkNode, NetworkEdge,
  NetworkResponse, NetworkSummary, PayerRow, LinkRole, LinkBasis, LinkResidual, LinkQualifier,
  RegimeToken,
} from './network.v1.js';

// ═══════════════════════════════════════════════════════════════════════════
// ROUND 7 — the person register (BUILD-BRIEF-R7 §1, §2, §4). A person has a raster of five blocks
// and 26 cells (`src/shared/person-raster.v1.ts`); read, estimated, declared and not_found values
// are `facts` rows with `subject_kind = 'person'`, computed values are one `persons_computed` row.
// This file carries no imports, so the three small unions below are spelled out — the module
// asserts at compile time that they equal its own.
// ═══════════════════════════════════════════════════════════════════════════

/** Mirrors `RasterCellType` (raster.v1.ts). */
export type PersonProfileCellType =
  | 'enum' | 'enum_multi' | 'band' | 'int' | 'number' | 'bool'
  | 'year' | 'month' | 'person_ref' | 'organisation_ref' | 'text';

/** Mirrors `PersonCellKind` (person-raster.v1.ts, §0.2): how a value may come to be. */
export type PersonProfileCellKind = 'estimate' | 'look_up' | 'source_or_leave' | 'computed' | 'declared';

/**
 * The state of one cell for one person (§3.2, §4). Five of them are mutually exclusive and one
 * — `held` — carries its method: an estimate is a held fact whose `method` is `estimated`.
 *   held            a current fact (read, estimated, declared) — value, method, as_of. The NAME
 *                   is about the record, not about the mark: whether anybody stood behind it is
 *                   the cell's own `held` flag (`confirmed_by`), and an unconfirmed one is ◇.
 *   pending         no fact, but a proposal in Review
 *   open            nothing on record — reads "not collected", never zero
 *   not_applicable  the cell is not asked of this person (`applies_when`)
 *   not_found       a run searched and said so — "not found · date"
 *   computed        `person_profile.compute` filled it — value and basis, NO claim mark
 */
export type PersonCellState = 'held' | 'pending' | 'open' | 'not_applicable' | 'not_found' | 'computed';

/** The evidence behind a held cell, as the page prints it under the value. */
export interface PersonProfileEvidence {
  kind: EvidenceKind | null;
  ref: string | null;
  quote: string | null;
}

/**
 * One person cell as `GET /api/persons/:id/profile` answers it (§4). `value === null` with state
 * `open` is "not collected" and must render as such — never a zero, never an empty string.
 */
export interface PersonProfileCell {
  /** `person.<block>.<cell>` */
  key: string;
  block: string;
  cell: string;
  /** The English label — the i18n key; the console re-labels through `personLabelFor`. */
  label: string;
  type: PersonProfileCellType;
  kind: PersonProfileCellKind;
  state: PersonCellState;
  /**
   * Whether the fact behind a `held` cell is CONFIRMED — `facts.confirmed_by IS NOT NULL`, the
   * operator's click or the hold rule's (`reviewed_by = 0` counts). ▪ when true, ◇ when false:
   * `state === 'held'` only says a current fact stands on the cell, and an import's unconfirmed
   * fact is exactly the ◇ the facts sheet offers a Confirm on. Always false for a computed cell —
   * arithmetic makes no claim and carries no mark at all (P12).
   */
  held: boolean;
  /** The canonical text `parsePersonCell` produced (as `facts.value` stores it), or the computed cell's value. */
  value: string | null;
  method: FactMethod | null;
  confidence: number | null;
  /** How exact a numeric value is; null = exact. */
  qualifier: 'at_least' | 'at_most' | 'approx' | null;
  as_of: string | null;
  /** For a computed cell: the sentence under the value («from title 'directrice'», «employer ORG-0012 is in VD»). */
  basis: string | null;
  evidence: PersonProfileEvidence | null;
  /** False when `applies_when` excludes this person — the cell reads "not applicable". */
  applies: boolean;
  fact_id?: number | null;
  /** Proposals in Review on this cell — the `pending` state's count. */
  pending?: number;
}

/** The five figures of §4 and §6.2 («n held · m estimated · k computed»). Arithmetic; no mark. */
export interface PersonProfileCoverage {
  held: number;
  estimated: number;
  computed: number;
  open: number;
  total: number;
  not_found?: number;
  not_applicable?: number;
}

export interface PersonProfileBlock {
  key: string;
  label: string;
  /** Every cell of the block, in raster order — including the ones nobody collected. */
  cells: PersonProfileCell[];
  coverage: PersonProfileCoverage;
}

/** GET /api/persons/:id/profile — the shape `ProfileZone`'s sibling consumes (§4). */
export interface PersonProfileResponse {
  person_id: number;
  person_ref: string;
  person_name: string;
  raster_version: string;
  blocks: PersonProfileBlock[];
  coverage: PersonProfileCoverage;
  /** When `person_profile.compute` last wrote this person; NULL until it has. */
  computed_at: string | null;
  /** How many cells a run may propose — the "of n cells" figure. */
  proposable: number;
}

/** One computed cell as `persons_computed.cells` stores it (§2): the value and the sentence the page prints under it. */
export interface PersonComputedCell {
  value: string;
  basis: string;
}

/** `persons_computed.cells` — keyed by the full cell key (`person.job.function_family`). */
export type PersonComputedCells = Record<string, PersonComputedCell>;

/** A row of `persons_computed` (§2). */
export interface PersonsComputedRow {
  person_id: number;
  cells: PersonComputedCells;
  computed_at: string;
}
